import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, ShoppingCart, Plus, Minus, Trash2, LogOut, PackageCheck, ClipboardList, ExternalLink, MessageSquareWarning, X, ImagePlus, HelpCircle, Send } from "lucide-react";
import { toast } from "sonner";
import portalApi from "../../lib/portalApi";

const STATUS_LABEL = {
  draft: "Neu / in Prüfung", confirmed: "Bestätigt", in_production: "In Bearbeitung",
  ready: "Versandbereit", shipped: "Versendet", cancelled: "Storniert",
};

const STEPS = ["Eingegangen", "Bestätigt", "In Bearbeitung", "Versandbereit", "Versendet"];
function stepIndex(o) {
  if (o.delivery_status === "shipped" || o.status === "shipped") return 4;
  if (o.status === "ready") return 3;
  if (o.status === "in_production") return 2;
  if (o.status === "confirmed") return 1;
  if (o.status === "cancelled") return -1;
  return 0;
}

const C_REASONS = [
  ["damaged", "Beschädigt"],
  ["wrong_item", "Falscher Artikel"],
  ["missing_qty", "Fehlende Menge"],
  ["other", "Sonstiges"],
];
const C_STATUS = { received: "Eingegangen", in_progress: "In Bearbeitung", resolved: "Abgeschlossen" };
const CONTACT_CATS = [
  ["order", "Bestellung"], ["delivery", "Lieferung"], ["invoice", "Rechnung"],
  ["product", "Produkt"], ["other", "Sonstiges"],
];
const FAQ_ITEMS = [
  { q: "Wie läuft eine Bestellung ab?", a: "Produkte in den Warenkorb legen, auf „Zur Vorschau“ tippen, die Übersicht mit Versandkosten und Lieferadresse prüfen und verbindlich bestellen. Sie erhalten eine Bestätigung, sobald wir Ihre Bestellung geprüft haben." },
  { q: "Wie lange dauert die Lieferung?", a: "Nach Bestätigung und Fertigung versenden wir in der Regel innerhalb von 2–5 Werktagen per DHL. Sendungsnummer und Status finden Sie unter „Meine Bestellungen“." },
  { q: "Welche Zahlungsarten gibt es?", a: "B2B-Bestellungen werden per Rechnung abgewickelt (Zahlungsziel laut Vereinbarung). Fragen zur Zahlung gerne über das Kontaktformular." },
  { q: "Ab wann ist der Versand kostenlos?", a: "Ab 100 € Warenwert (netto) liefern wir versandkostenfrei. Darunter werden die Versandkosten nach Gewicht berechnet und in der Bestellübersicht separat ausgewiesen." },
];
const C_STATUS_STYLE = {
  received: "bg-amber-100 text-amber-800",
  in_progress: "bg-blue-100 text-blue-800",
  resolved: "bg-green-100 text-green-700",
};
const MAX_FILES = 6;

function eur(v) {
  return (Number(v) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function PortalComplaintImage({ imageId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true; let objUrl;
    portalApi.get(`/complaints/image/${imageId}`, { responseType: "blob" })
      .then((r) => { if (active) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [imageId]);
  if (!url) return <div className="h-16 w-16 rounded-lg bg-neutral-100 animate-pulse" />;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`portal-complaint-image-${imageId}`}>
      <img src={url} alt="Reklamation" className="h-16 w-16 rounded-lg object-cover border border-neutral-200 hover:opacity-80" />
    </a>
  );
}

function ComplaintDialog({ order, onClose, onDone }) {
  const [reason, setReason] = useState("damaged");
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const next = [...files];
    for (const f of incoming) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: nur Bilder erlaubt`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: größer als 10 MB`); continue; }
      if (next.length >= MAX_FILES) { toast.error(`Maximal ${MAX_FILES} Bilder`); break; }
      next.push(f);
    }
    setFiles(next);
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("order_id", order.id);
      fd.append("reason", reason);
      fd.append("description", desc || "");
      files.forEach((f) => fd.append("files", f));
      const { data } = await portalApi.post("/complaints", fd);
      toast.success(`Reklamation ${data.number} übermittelt`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reklamation fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="portal-complaint-dialog">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <div className="font-black text-lg">Reklamation melden</div>
            <div className="text-xs text-neutral-400">Bestellung {order.number}</div>
          </div>
          <button onClick={onClose} data-testid="portal-complaint-close" className="h-9 w-9 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-neutral-500 block mb-1">Grund</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="portal-complaint-reason"
                    className="w-full rounded-xl border border-neutral-300 py-2.5 px-3 text-sm font-semibold bg-white">
              {C_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 block mb-1">Beschreibung</label>
            <textarea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="portal-complaint-desc"
                      placeholder="Was ist mit der Bestellung nicht in Ordnung?"
                      className="w-full rounded-xl border border-neutral-300 p-3 text-sm outline-none focus:border-black" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 block mb-1">Fotos (optional · max. {MAX_FILES} · je max. 10 MB)</label>
            <div className="grid grid-cols-3 gap-2">
              {files.map((f, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-neutral-200" data-testid={`portal-complaint-preview-${i}`}>
                  <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
                  <button onClick={() => removeFile(i)} data-testid={`portal-complaint-remove-${i}`}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={13} /></button>
                </div>
              ))}
              {files.length < MAX_FILES && (
                <label className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-400 cursor-pointer hover:border-black hover:text-black" data-testid="portal-complaint-add-file">
                  <ImagePlus size={22} />
                  <span className="text-[10px] mt-1 font-semibold">Bild</span>
                  <input type="file" accept="image/*" multiple className="hidden"
                         onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-neutral-100 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-xl border border-neutral-300 py-3 font-semibold hover:bg-neutral-50">Abbrechen</button>
          <button onClick={submit} disabled={submitting} data-testid="portal-complaint-submit"
                  className="flex-1 rounded-xl bg-[var(--pop-pink,#e5006d)] text-white py-3 font-bold hover:opacity-90 disabled:opacity-50">
            {submitting ? "Senden…" : "Reklamation senden"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Portal() {
  const nav = useNavigate();
  const [tab, setTab] = useState("shop");
  const [customer, setCustomer] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [offers, setOffers] = useState([]);
  const [cart, setCart] = useState({}); // key "item|bundle" -> {item_id, bundle_code, qty}
  const [sel, setSel] = useState({});   // item_id -> bundle code ("single" default)
  const [orders, setOrders] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [complaintOrder, setComplaintOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [rows120, setRows120] = useState({});
  const getRows120 = (itemId) => { const r = rows120[itemId]; return (r && r.length === 6) ? r : Array(6).fill(itemId); };
  const setRow120 = (itemId, idx, val) => setRows120((s) => { const cur = (s[itemId] && s[itemId].length === 6 ? [...s[itemId]] : Array(6).fill(itemId)); cur[idx] = val; return { ...s, [itemId]: cur }; });
  const price120 = (itemId) => getRows120(itemId).reduce((s, rid) => {
    const r = catalog.find((c) => c.id === rid); return s + ((r?.price) || 0) * 20;
  }, 0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [contactCat, setContactCat] = useState("order");
  const [contactMsg, setContactMsg] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const [shipThreshold, setShipThreshold] = useState(100);
  const faqItems = FAQ_ITEMS.map((f, i) => i === 3
    ? { ...f, a: `Ab ${eur(shipThreshold)} Warenwert (netto) liefern wir versandkostenfrei. Darunter werden die Versandkosten nach Gewicht berechnet und in der Bestellübersicht separat ausgewiesen.` }
    : f);

  const reloadComplaints = async () => {
    try { const r = await portalApi.get("/complaints"); setComplaints(r.data); } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    if (!localStorage.getItem("popcrn_portal_token")) { nav("/portal/login"); return; }
    (async () => {
      try {
        const [me, cat, ord, cmp, ofs, si] = await Promise.all([
          portalApi.get("/me"), portalApi.get("/catalog"), portalApi.get("/orders"), portalApi.get("/complaints"), portalApi.get("/offers"), portalApi.get("/shipping-info"),
        ]);
        setCustomer(me.data); setCatalog(cat.data); setOrders(ord.data); setComplaints(cmp.data); setOffers(ofs.data || []);
        setShipThreshold(si.data?.free_threshold_net ?? 100);
      } catch (_e) { /* interceptor handles 401 */ }
      finally { setLoading(false); }
    })();
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("popcrn_portal_token");
    localStorage.removeItem("popcrn_portal_customer");
    nav("/portal/login");
  };

  const bcode = (it) => (sel[it.id] && sel[it.id] !== "single" ? sel[it.id] : null);
  const optOf = (it, code) => (it.bundle_options || []).find((o) => (o.code || "single") === (code || "single"))
    || { code: null, price: it.price, label: "Einzeln" };
  const keyOf = (itemId, code) => `${itemId}|${code || "single"}`;

  const setQty = (key, meta, qty) => {
    setCart((c) => {
      const n = { ...c };
      if (qty <= 0) delete n[key]; else n[key] = { ...meta, qty };
      return n;
    });
  };

  const cartLines = Object.values(cart).map((e) => {
    const it = catalog.find((x) => x.id === e.item_id);
    if (!it) return null;
    const opt = optOf(it, e.bundle_code);
    const unitPrice = (e.bundle_code === "120er") ? price120(e.item_id) : (opt.price || 0);
    return { it, opt, qty: e.qty, bundle_code: e.bundle_code || null, unitPrice };
  }).filter(Boolean);
  const cartTotal = cartLines.reduce((s, l) => s + (l.unitPrice || 0) * l.qty, 0);
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);

  const buildLines = () => cartLines.map((l) => ({
    item_id: l.it.id, quantity: l.qty, bundle_code: l.bundle_code || null,
    display_rows: l.bundle_code === "120er" ? getRows120(l.it.id).map((id) => ({ item_id: id, quantity: 20 })) : null,
  }));

  const openPreview = async () => {
    if (!cartLines.length) { toast.error("Warenkorb ist leer"); return; }
    setQuoting(true); setPreviewOpen(true); setQuote(null);
    try {
      const { data } = await portalApi.post("/quote", { lines: buildLines(), notes: notes || null });
      setQuote(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
      setPreviewOpen(false);
    } finally { setQuoting(false); }
  };

  const confirmOrder = async () => {
    setSubmitting(true);
    try {
      const { data } = await portalApi.post("/orders", { lines: buildLines(), notes: notes || null });
      toast.success(`Bestellung ${data.number} übermittelt`);
      setCart({}); setNotes(""); setPreviewOpen(false); setQuote(null);
      const ord = await portalApi.get("/orders");
      setOrders(ord.data);
      setTab("orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bestellung fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  const sendContact = async () => {
    if (!contactMsg.trim()) { toast.error("Bitte eine Nachricht eingeben"); return; }
    setContactSending(true);
    try {
      await portalApi.post("/contact", { category: contactCat, message: contactMsg });
      toast.success("Nachricht gesendet – wir melden uns!");
      setContactMsg("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally { setContactSending(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-500">Lädt…</div>;
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="portal-home">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#0f0f10] text-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-[var(--pop-yellow,#ffd400)] flex items-center justify-center">
            <ShoppingBag size={18} className="text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black leading-tight">POPCRN Portal</div>
            <div className="text-xs text-neutral-400 truncate">{customer?.name}</div>
          </div>
          <button onClick={logout} data-testid="portal-logout"
                  className="text-sm inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1.5 hover:bg-neutral-800">
            <LogOut size={14} /> Abmelden
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
          {[["shop", "Produkte", ShoppingCart], ["orders", "Meine Bestellungen", ClipboardList], ["help", "Hilfe & Kontakt", HelpCircle]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`portal-tab-${k}`}
                    className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-2.5 text-sm font-semibold border-b-2 inline-flex items-center gap-2 ${tab === k ? "border-[var(--pop-yellow,#ffd400)] text-white" : "border-transparent text-neutral-400"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-40">
        {tab === "shop" && (
          <>
            {offers.length > 0 && (
              <div className="mb-4 rounded-xl bg-[var(--pop-yellow,#ffd400)] text-black px-4 py-3 font-semibold text-sm flex flex-wrap gap-x-5 gap-y-1" data-testid="portal-offer-banner">
                {offers.map((o, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5">
                    🎉 {o.name}: -{o.discount_type === "percent" ? `${o.discount_value}%` : eur(o.discount_value)}
                    {o.ends_at ? ` · bis ${new Date(o.ends_at).toLocaleDateString("de-DE")}` : ""}
                  </span>
                ))}
              </div>
            )}
            {catalog.length === 0 ? (
              <div className="text-center text-neutral-500 py-20" data-testid="portal-empty-catalog">
                Für Ihr Konto sind noch keine Produkte freigegeben.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {catalog.map((it) => {
                  const code = bcode(it);
                  const opt = optOf(it, code);
                  const key = keyOf(it.id, code);
                  const qtyInCart = cart[key]?.qty || 0;
                  const meta = { item_id: it.id, bundle_code: code };
                  const hasBundles = (it.bundle_options || []).length > 1;
                  return (
                  <div key={it.id} className="rounded-2xl bg-white border border-neutral-200 p-4 flex flex-col" data-testid={`portal-product-${it.sku}`}>
                    <div className="font-bold text-neutral-900">{it.name}</div>
                    <div className="text-xs text-neutral-400 font-mono mb-2">{it.sku}</div>
                    {it.offer && (
                      <span data-testid={`portal-offer-${it.sku}`} className="inline-flex items-center self-start mb-2 px-2 py-0.5 rounded-full bg-[var(--pop-yellow,#ffd400)] text-black text-[11px] font-bold">
                        Aktion {it.offer.discount_type === "percent" ? `-${it.offer.discount_value}%` : `-${eur(it.offer.discount_value)}`}                      </span>
                    )}
                    {hasBundles && (
                      <div className="mb-2">
                        <label className="text-[11px] text-neutral-400 block mb-1">Einheit</label>
                        <select value={sel[it.id] || "single"} onChange={(e) => setSel((s) => ({ ...s, [it.id]: e.target.value }))}
                                data-testid={`portal-bundle-${it.sku}`}
                                className="w-full rounded-xl border border-neutral-300 py-2 px-2 text-sm font-semibold bg-white">
                          {(it.bundle_options || []).map((o) => (
                            <option key={o.code || "single"} value={o.code || "single"}>{o.label}{o.code === "120er" ? ` · ${eur(price120(it.id))}` : ` · ${eur(o.price)}`}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {code === "120er" && (
                      <div className="mb-2 rounded-xl border border-neutral-200 bg-neutral-50 p-2 space-y-1" data-testid={`portal-display120-${it.sku}`}>
                        <div className="text-[11px] font-semibold text-neutral-500">120er-Display · 6 Reihen × 20 Stk</div>
                        {getRows120(it.id).map((rid, j) => (
                          <div key={j} className="flex items-center gap-2">
                            <span className="text-[11px] w-12 shrink-0 text-neutral-400">Reihe {j + 1}</span>
                            <select value={rid} onChange={(e) => setRow120(it.id, j, e.target.value)}
                                    data-testid={`portal-display120-${it.sku}-row-${j}`}
                                    className="flex-1 min-w-0 rounded-lg border border-neutral-300 py-1.5 px-2 text-xs bg-white">
                              {catalog.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="text-lg font-black mb-3" data-testid={`portal-price-${it.sku}`}>
                      {code === "120er" ? (
                        <>{eur(price120(it.id))} <span className="text-xs font-normal text-neutral-400">/ 120er Display</span></>
                      ) : (
                        <>
                          {opt.list_price != null && opt.list_price > opt.price && (
                            <span className="text-sm font-normal text-neutral-400 line-through mr-1.5">{eur(opt.list_price)}</span>
                          )}
                          {eur(opt.price)} <span className="text-xs font-normal text-neutral-400">/ {opt.code ? opt.label : (it.unit || "Stk")}</span>
                        </>
                      )}
                    </div>
                    <div className="mt-auto">
                      {qtyInCart ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(key, meta, qtyInCart - 1)} data-testid={`portal-dec-${it.sku}`} className="h-9 w-9 rounded-xl bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Minus size={16} /></button>
                          <input type="number" min="0" value={qtyInCart} onChange={(e) => setQty(key, meta, Math.max(0, parseInt(e.target.value || "0", 10)))}
                                 data-testid={`portal-qty-${it.sku}`}
                                 className="w-16 text-center rounded-xl border border-neutral-300 py-1.5 font-bold" />
                          <button onClick={() => setQty(key, meta, qtyInCart + 1)} data-testid={`portal-inc-${it.sku}`} className="h-9 w-9 rounded-xl bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><Plus size={16} /></button>
                        </div>
                      ) : (
                        <button onClick={() => setQty(key, meta, 1)} data-testid={`portal-add-${it.sku}`}
                                className="w-full rounded-xl bg-black text-white py-2.5 font-bold hover:bg-neutral-800 flex items-center justify-center gap-2">
                          <Plus size={16} /> Hinzufügen
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="portal-orders">
            {orders.length === 0 ? (
              <div className="text-center text-neutral-500 py-20">Noch keine Bestellungen.</div>
            ) : orders.map((o) => {
              const si = stepIndex(o);
              const cancelled = o.status === "cancelled";
              return (
              <div key={o.id} className="rounded-2xl bg-white border border-neutral-200 p-4" data-testid={`portal-order-${o.number}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-neutral-100 flex items-center justify-center"><PackageCheck size={18} className="text-neutral-500" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{o.number}</div>
                    <div className="text-xs text-neutral-400">{(o.created_at || "").slice(0, 10)} · {o.line_count} Position(en)</div>
                  </div>
                  <span className={`text-xs font-semibold rounded-full px-3 py-1.5 ${cancelled ? "bg-red-100 text-red-700" : si >= 4 ? "bg-green-100 text-green-700" : "bg-neutral-100"}`} data-testid={`portal-order-status-${o.number}`}>{STATUS_LABEL[o.status] || o.status}</span>
                </div>
                {!cancelled && (
                  <div className="mt-4 flex items-center" data-testid={`portal-stepper-${o.number}`}>
                    {STEPS.map((s, i) => (
                      <div key={s} className="flex-1 flex flex-col items-center relative">
                        {i > 0 && <div className={`absolute right-1/2 top-2 h-0.5 w-full ${i <= si ? "bg-[var(--pop-pink,#e5006d)]" : "bg-neutral-200"}`} />}
                        <div className={`relative z-10 h-4 w-4 rounded-full border-2 ${i <= si ? "bg-[var(--pop-pink,#e5006d)] border-[var(--pop-pink,#e5006d)]" : "bg-white border-neutral-300"}`} />
                        <div className={`mt-1 text-[10px] text-center leading-tight ${i <= si ? "text-neutral-800 font-semibold" : "text-neutral-400"}`}>{s}</div>
                      </div>
                    ))}
                  </div>
                )}
                {o.tracking_number && (
                  <div className="mt-3 text-xs bg-neutral-50 border border-neutral-200 rounded-xl px-3 py-2" data-testid={`portal-tracking-${o.number}`}>
                    <span className="text-neutral-500">Sendungsnummer{o.carrier ? ` (${o.carrier})` : ""}: </span>
                    {o.tracking_url ? (
                      <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="font-mono font-semibold text-[var(--pop-pink,#e5006d)] underline inline-flex items-center gap-1" data-testid={`portal-tracking-link-${o.number}`}>
                        {o.tracking_number}<ExternalLink size={12} />
                      </a>
                    ) : (
                      <span className="font-mono font-semibold">{o.tracking_number}</span>
                    )}
                  </div>
                )}
                {o.can_complain && (
                  <div className="mt-3 flex justify-end">
                    <button onClick={() => setComplaintOrder(o)} data-testid={`portal-complain-btn-${o.number}`}
                            className="text-sm inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-3.5 py-1.5 font-semibold text-neutral-700 hover:border-[var(--pop-pink,#e5006d)] hover:text-[var(--pop-pink,#e5006d)]">
                      <MessageSquareWarning size={14} /> Reklamation melden
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {tab === "help" && (
          <div className="space-y-6" data-testid="portal-help">
            <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="portal-help-faq">
              <h3 className="font-black text-base mb-3 flex items-center gap-2"><HelpCircle size={18} /> Informationen zum Bestellvorgang</h3>
              <div className="space-y-3 text-sm">
                {faqItems.map((f, i) => (
                  <div key={i} data-testid={`portal-faq-${i}`}>
                    <div className="font-semibold text-neutral-900">{f.q}</div>
                    <div className="text-neutral-600 whitespace-pre-line">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-neutral-200 p-5" data-testid="portal-contact-form">
              <h3 className="font-black text-base mb-3 flex items-center gap-2"><Send size={16} /> Kontaktformular</h3>
              <label className="text-xs text-neutral-400 block mb-1">Anliegen</label>
              <select value={contactCat} onChange={(e) => setContactCat(e.target.value)} data-testid="portal-contact-category"
                      className="w-full rounded-xl border border-neutral-300 py-2.5 px-2 text-sm font-semibold bg-white mb-3">
                {CONTACT_CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="text-xs text-neutral-400 block mb-1">Ihre Nachricht</label>
              <textarea rows={4} value={contactMsg} onChange={(e) => setContactMsg(e.target.value)} data-testid="portal-contact-message"
                        placeholder="Wie können wir helfen?"
                        className="w-full rounded-xl border border-neutral-300 py-2.5 px-3 text-sm outline-none focus:border-black mb-3" />
              <button onClick={sendContact} disabled={contactSending} data-testid="portal-contact-send"
                      className="rounded-xl bg-black text-white px-5 py-2.5 font-bold hover:bg-neutral-800 disabled:opacity-50 inline-flex items-center gap-2">
                <Send size={15} /> {contactSending ? "Senden…" : "Nachricht senden"}
              </button>
            </div>

            <div data-testid="portal-complaints-wrap">
              <h3 className="font-black text-base mb-3 flex items-center gap-2"><MessageSquareWarning size={18} /> Reklamationen</h3>
              <div className="space-y-3" data-testid="portal-complaints">
            {complaints.length === 0 ? (
              <div className="text-center text-neutral-500 py-20" data-testid="portal-complaints-empty">
                <MessageSquareWarning size={30} className="mx-auto mb-3 opacity-40" />
                Noch keine Reklamationen. Reklamationen können für versendete Bestellungen gemeldet werden.
              </div>
            ) : complaints.map((c) => (
              <div key={c.id} className="rounded-2xl bg-white border border-neutral-200 p-4" data-testid={`portal-complaint-${c.number}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[var(--pop-pink,#e5006d)]/10 flex items-center justify-center"><MessageSquareWarning size={18} className="text-[var(--pop-pink,#e5006d)]" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{c.number} <span className="text-xs font-normal text-neutral-400">· {c.reason_label}</span></div>
                    <div className="text-xs text-neutral-400">Bestellung {c.order_number || "—"} · {(c.created_at || "").slice(0, 10)}</div>
                  </div>
                  <span className={`text-xs font-semibold rounded-full px-3 py-1.5 ${C_STATUS_STYLE[c.status] || "bg-neutral-100"}`} data-testid={`portal-complaint-status-${c.number}`}>
                    {c.status_label || C_STATUS[c.status] || c.status}
                  </span>
                </div>
                {c.description && <div className="mt-3 text-sm bg-neutral-50 border border-neutral-200 rounded-xl p-3 whitespace-pre-wrap">{c.description}</div>}
                {(c.images || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid={`portal-complaint-images-${c.number}`}>
                    {c.images.map((im) => <PortalComplaintImage key={im.image_id} imageId={im.image_id} />)}
                  </div>
                )}
                {c.admin_reply && (
                  <div className="mt-3 text-sm bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <div className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide mb-1">Antwort von POPCRN</div>
                    <div className="whitespace-pre-wrap">{c.admin_reply}</div>
                  </div>
                )}
              </div>
            ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Cart bar */}
      {tab === "shop" && cartCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-neutral-200 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]" data-testid="portal-cart-bar">
          <div className="max-w-5xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="text-xs text-neutral-400">{cartCount} Artikel</div>
                <div className="font-black text-lg">{eur(cartTotal)}</div>
              </div>
              <button onClick={() => setCart({})} className="text-sm text-neutral-500 inline-flex items-center gap-1 hover:text-black" data-testid="portal-cart-clear">
                <Trash2 size={14} /> leeren
              </button>
              <button onClick={openPreview} data-testid="portal-submit-order"
                      className="rounded-xl bg-[var(--pop-pink,#e5006d)] text-white px-6 py-3 font-bold hover:opacity-90 disabled:opacity-50">
                Zur Vorschau
              </button>
            </div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anmerkung (optional)…"
                   data-testid="portal-order-notes"
                   className="mt-2 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="portal-preview-dialog">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-neutral-200 sticky top-0 bg-white">
              <div className="font-black text-lg">Bestellübersicht</div>
              <button onClick={() => setPreviewOpen(false)} data-testid="portal-preview-close" className="h-9 w-9 rounded-full bg-neutral-100 flex items-center justify-center hover:bg-neutral-200"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-4">
              {quoting || !quote ? (
                <div className="py-10 text-center text-neutral-500" data-testid="portal-preview-loading">Berechne…</div>
              ) : (
                <>
                  <div className="space-y-2" data-testid="portal-preview-lines">
                    {quote.lines.map((l, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 text-sm border-b border-neutral-100 pb-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-neutral-900">{l.name}{l.bundle_label ? ` · ${l.bundle_label}` : ""}</div>
                          <div className="text-xs text-neutral-400">{l.quantity} × {eur(l.price)}{l.bundle_code ? " / Einheit" : ` / ${l.unit || "Stk"}`}</div>
                        </div>
                        <div className="font-bold whitespace-nowrap">{eur(l.line_total)}</div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1.5 text-sm" data-testid="portal-preview-totals">
                    <div className="flex justify-between"><span className="text-neutral-500">Warenwert (netto)</span><span className="font-semibold">{eur(quote.goods_value_net)}</span></div>
                    {quote.shipping.free ? (
                      <div className="flex justify-between text-green-700"><span>Versand</span><span className="font-semibold" data-testid="portal-preview-shipping">Kostenlos</span></div>
                    ) : (
                      <>
                        <div className="flex justify-between"><span className="text-neutral-500">Versand{quote.shipping.class ? ` (${quote.shipping.class})` : ""} (netto)</span><span className="font-semibold" data-testid="portal-preview-shipping">{eur(quote.shipping.net)}</span></div>
                        <div className="flex justify-between text-neutral-400 text-xs"><span>zzgl. {Math.round((quote.shipping.vat_rate || 0) * 100)}% MwSt. Versand</span><span>{eur(quote.shipping.vat)}</span></div>
                      </>
                    )}
                    <div className="flex justify-between text-base font-black pt-1.5 border-t border-neutral-200"><span>Gesamt (netto)</span><span data-testid="portal-preview-total">{eur(quote.total_net)}</span></div>
                    {!quote.shipping.free && (
                      <div className="text-[11px] text-neutral-400">Versandkostenfrei ab {eur(quote.shipping.threshold_net)} Warenwert (netto).</div>
                    )}
                  </div>
                  <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-sm" data-testid="portal-preview-address">
                    <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wide mb-1">Lieferadresse</div>
                    <div className="font-semibold">{quote.delivery_address.name}</div>
                    <div className="text-neutral-600">{quote.delivery_address.street}</div>
                    <div className="text-neutral-600">{quote.delivery_address.zip} {quote.delivery_address.city}</div>
                  </div>
                  {notes && <div className="text-sm text-neutral-600"><span className="font-semibold">Anmerkung:</span> {notes}</div>}
                </>
              )}
            </div>
            <div className="p-4 border-t border-neutral-200 sticky bottom-0 bg-white flex gap-2">
              <button onClick={() => setPreviewOpen(false)} data-testid="portal-preview-back" className="flex-1 rounded-xl border border-neutral-300 py-3 font-bold hover:bg-neutral-50">Zurück</button>
              <button onClick={confirmOrder} disabled={quoting || !quote || submitting} data-testid="portal-preview-confirm" className="flex-1 rounded-xl bg-[var(--pop-pink,#e5006d)] text-white py-3 font-bold hover:opacity-90 disabled:opacity-50">{submitting ? "Senden…" : "Verbindlich bestellen"}</button>
            </div>
          </div>
        </div>
      )}

      {complaintOrder && (
        <ComplaintDialog order={complaintOrder}
                         onClose={() => setComplaintOrder(null)}
                         onDone={async () => { setComplaintOrder(null); await reloadComplaints(); setTab("help"); }} />
      )}
    </div>
  );
}
